/**
 * Canonical file extension per mime type.
 *
 * Blobstore writes an asset's `mime` alongside its asset id and serves it from signed urls that
 * carry no extension, so this is what names a downloaded file.
 */
export const MIME_TYPE_TO_FILE_EXTENSION: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg', // Non-standard, but still sent by some providers.
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/jxl': 'jxl',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/tiff': 'tiff',
  'image/bmp': 'bmp',
  'image/svg+xml': 'svg',

  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
  'video/mpeg': 'mpeg',
  'video/x-m4v': 'm4v',
  'video/x-matroska': 'mkv',

  'audio/mpeg': 'mp3',
  'audio/mp4': 'm4a',
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/ogg': 'ogg',
  'audio/webm': 'weba',
  'audio/flac': 'flac',
  'audio/aac': 'aac',

  'model/gltf-binary': 'glb',
  'model/gltf+json': 'gltf',
  'model/obj': 'obj',
  'model/fbx': 'fbx',
  'model/ply': 'ply',
};

/** The extension to fall back on when the mime is absent or unmapped. */
const defaultExtensionForType = (fileType: string): string => {
  switch (fileType) {
    case 'image':
      return 'png';
    case 'video':
      return 'mp4';
    default:
      return '';
  }
};

/**
 * Extension for a Blobstore-served asset, whose signed url carries none.
 */
export const extensionFromMimeType = (mimeType: string | undefined, fileType: string): string =>
  (mimeType && MIME_TYPE_TO_FILE_EXTENSION[mimeType.toLowerCase()]) || defaultExtensionForType(fileType);
